import { motion } from 'framer-motion'
import { useInView } from 'framer-motion'
import { useRef } from 'react'
import { CheckCircle, Target, Users, Award, TrendingUp, Shield, Zap } from 'lucide-react'
import { useTranslation } from 'react-i18next'

const About = () => {
  const ref = useRef(null)
  const storyRef = useRef(null)
  const isInView = useInView(ref, { once: true, margin: "-100px" })
  const isStoryInView = useInView(storyRef, { once: true, margin: "-80px" })
  const { t } = useTranslation()

  const industries = [
    t('aboutPage.industries.healthcare'),
    t('aboutPage.industries.government'),
    t('aboutPage.industries.tax'),
    t('aboutPage.industries.registers'),
    t('aboutPage.industries.procurement'),
    t('aboutPage.industries.budget'),
    t('aboutPage.industries.municipal'),
    t('aboutPage.industries.education'),
    t('aboutPage.industries.construction'),
    t('aboutPage.industries.transport'),
    t('aboutPage.industries.interaction'),
    t('aboutPage.industries.economy')
  ]

  const values = [
    {
      icon: Target,
      title: t('aboutPage.values.expertise.title'),
      description: t('aboutPage.values.expertise.description')
    },
    {
      icon: Users,
      title: t('aboutPage.values.team.title'),
      description: t('aboutPage.values.team.description')
    },
    {
      icon: Award,
      title: t('aboutPage.values.quality.title'),
      description: t('aboutPage.values.quality.description')
    },
    {
      icon: TrendingUp,
      title: t('aboutPage.values.innovation.title'),
      description: t('aboutPage.values.innovation.description')
    },
    {
      icon: Shield,
      title: t('aboutPage.values.reliability.title'),
      description: t('aboutPage.values.reliability.description')
    },
    {
      icon: Zap,
      title: t('aboutPage.values.speed.title'),
      description: t('aboutPage.values.speed.description')
    }
  ]

  return (
    <div className="pt-16 overflow-hidden">
      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-br from-primary-50 via-white to-accent-50">
        <div className="container-max">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-primary-100 text-primary-700 text-sm font-medium mb-6">
              <Award className="w-4 h-4 mr-2" />
              {t('aboutPage.title')}
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              <span className="gradient-text">{t('aboutPage.headline1')}</span>
              {t('aboutPage.headline')}
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              {t('aboutPage.description')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Company Story */}
      <section ref={storyRef} className="section-padding bg-white">
        <div className="container-max">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            {/* Brand visual */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={isStoryInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.7 }}
              className="lg:col-span-5"
            >
              <div className="relative sticky top-28">
                <div className="absolute -inset-4 bg-gradient-to-br from-primary-100 via-white to-accent-100 rounded-3xl blur-sm" />
                <div className="relative rounded-3xl overflow-hidden bg-[#001B44] aspect-square flex items-center justify-center p-10 shadow-2xl">
                  <img
                    src="/IMG_1983.PNG"
                    alt="InfotechPro logo"
                    className="w-full h-full object-contain"
                  />
                </div>
                <p className="mt-6 text-center text-sm font-medium tracking-wide text-gray-500 uppercase">
                  {t('aboutPage.story.badge')}
                </p>
              </div>
            </motion.div>

            {/* Text content */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={isStoryInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="lg:col-span-7 space-y-10"
            >
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                  {t('aboutPage.story.title')}
                </h2>
                <p className="text-lg md:text-xl text-gray-600 leading-relaxed">
                  {t('aboutPage.story.who.description')}
                </p>
              </div>

              <div className="border-l-4 border-primary-500 pl-6 py-1">
                <p className="text-lg text-gray-700 leading-relaxed">
                  {t('aboutPage.story.park.description')}
                </p>
              </div>

              <div className="bg-gradient-to-br from-gray-50 to-primary-50/40 rounded-2xl p-8 border border-gray-100">
                <p className="text-xl md:text-2xl font-semibold text-gray-900 leading-snug mb-4">
                  {t('aboutPage.story.quality.quote')}
                </p>
                <p className="text-lg text-gray-600 leading-relaxed">
                  {t('aboutPage.story.quality.description')}
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="section-padding bg-gray-50">
        <div className="container-max">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-stretch">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8 }}
              className="h-full"
            >
              <div className="bg-gradient-to-br from-primary-600 to-accent-600 rounded-2xl p-8 text-white h-full flex flex-col">
                <Target className="w-12 h-12 mb-6 flex-shrink-0" />
                <h2 className="text-2xl font-bold mb-4">{t('aboutPage.mission.title')}</h2>
                <p className="text-primary-100 leading-relaxed flex-1">
                  {t('aboutPage.mission.description')}
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="h-full"
            >
              <div className="bg-gradient-to-br from-accent-600 to-purple-600 rounded-2xl p-8 text-white h-full flex flex-col">
                <TrendingUp className="w-12 h-12 mb-6 flex-shrink-0" />
                <h2 className="text-2xl font-bold mb-4">{t('aboutPage.vision.title')}</h2>
                <p className="text-accent-100 leading-relaxed flex-1">
                  {t('aboutPage.vision.description')}
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Industries */}
      <section ref={ref} className="section-padding bg-white">
        <div className="container-max">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('aboutPage.industries.title')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('aboutPage.industries.description')}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {industries.map((industry, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.1 * index, duration: 0.4 }}
                className="bg-white rounded-lg p-6 shadow-md border border-gray-100 card-hover"
              >
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-primary-600 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 font-medium">{industry}</span>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Values */}
      <section className="section-padding bg-gray-50">
        <div className="container-max">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('aboutPage.values.title')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('aboutPage.values.description')}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {values.map((value, index) => {
              const IconComponent = value.icon
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: index * 0.1, duration: 0.6 }}
                  className="text-center"
                >
                  <div className="w-16 h-16 bg-gradient-to-r from-primary-500 to-accent-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <IconComponent className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">
                    {value.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {value.description}
                  </p>
                </motion.div>
              )
            })}
          </div>
        </div>
      </section>


    </div>
  )
}

export default About
